import React, { useState, useEffect } from "react";
import FormField from "../shared/FormField";
import Button from "../shared/Button";
import "./EventForm.css";
import {
  combineDateTime,
  CreateEventDTO,
  EventFormData,
  ShortfyEvent,
  splitDateTime,
  validateEventForm,
} from "../../types/event.types";
import { createEvent, updateEvent } from "../../hooks/useEvent";
import { useNotification } from "../../context/NotificationContext";

interface EventFormProps {
  event?: ShortfyEvent;
  onSuccess?: (event: ShortfyEvent) => void;
  onCancel?: () => void;
}

const initialFormData: EventFormData = {
  eventName: "",
  description: "",
  eventStartDate: "",
  eventStartTime: "",
  eventEndDate: "",
  eventEndTime: "",
  location: "",
  maxCapacity: 50,
  mealOptionsEnabled: false,
  additionalGuestsEnabled: false,
  registrationDeadline: "",
  registrationDeadlineTime: "23:59",
};

const EventForm: React.FC<EventFormProps> = ({
  event,
  onSuccess,
  onCancel,
}) => {
  const [formData, setFormData] = useState<EventFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { setNotification } = useNotification();
  const isEditMode = !!event;

  useEffect(() => {
    if (!event) return;

    const startDateTime = splitDateTime(event.eventStartDate);
    const endDateTime = splitDateTime(event.eventEndDate);
    const deadlineDateTime = event.registrationDeadline
      ? splitDateTime(event.registrationDeadline)
      : { date: "", time: "23:59" };

    setFormData({
      eventName: event.eventName,
      description: event.description || "",
      eventStartDate: startDateTime.date,
      eventStartTime: startDateTime.time,
      eventEndDate: endDateTime.date,
      eventEndTime: endDateTime.time,
      location: event.location,
      maxCapacity: event.maxCapacity,
      mealOptionsEnabled: event.mealOptionsEnabled,
      additionalGuestsEnabled: event.additionalGuestsEnabled ?? false,
      registrationDeadline: deadlineDateTime.date,
      registrationDeadlineTime: deadlineDateTime.time,
    });
  }, [event]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : type === "number"
            ? parseInt(value, 10) || 0
            : value,
    }));
  };

  const getDefaultDeadlineIso = (eventStartIso: string) => {
    const eventStartDt = new Date(eventStartIso);
    const deadlineDt = new Date(eventStartDt.getTime() - 60 * 60 * 1000);
    return deadlineDt.toISOString();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateEventForm(formData);
    if (validationErrors.length > 0) {
      setNotification({
        type: "error",
        message: validationErrors.join(". "),
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const eventStartDateIso = combineDateTime(
        formData.eventStartDate,
        formData.eventStartTime,
      );

      const eventEndDateIso = combineDateTime(
        formData.eventEndDate,
        formData.eventEndTime,
      );

      const registrationDeadlineIso = formData.registrationDeadline
        ? combineDateTime(
            formData.registrationDeadline,
            formData.registrationDeadlineTime || "23:59",
          )
        : getDefaultDeadlineIso(eventStartDateIso);

      const dto: CreateEventDTO = {
        eventName: formData.eventName.trim(),
        description: formData.description.trim(),
        eventStartDate: eventStartDateIso,
        eventEndDate: eventEndDateIso,
        location: formData.location.trim(),
        maxCapacity: formData.maxCapacity,
        mealOptionsEnabled: formData.mealOptionsEnabled,
        additionalGuestsEnabled: formData.additionalGuestsEnabled,
        registrationDeadline: registrationDeadlineIso,
      };

      const response =
        isEditMode && event
          ? await updateEvent(event.eventId, dto)
          : await createEvent(dto);

      if (response.success && response.data) {
        setNotification({
          type: "success",
          message: isEditMode
            ? "Event updated successfully!"
            : "Event created successfully!",
        });

        if (!isEditMode) setFormData(initialFormData);

        onSuccess?.(response.data);
      } else {
        const errorMessages = response.errors || [
          response.error || "An error occurred",
        ];
        setNotification({
          type: "error",
          message: errorMessages.join(". "),
        });
      }
    } catch {
      setNotification({
        type: "error",
        message: "An unexpected error occurred",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <form onSubmit={handleSubmit} className="eventForm">
      <div className="eventFormGrid">
        <div className="eventFormFullWidth">
          <FormField
            label="Event Name"
            name="eventName"
            value={formData.eventName}
            onChange={handleChange}
            placeholder="e.g., Summer Company Picnic"
            required
            maxLength={100}
          />
        </div>

        <div className="eventFormFullWidth">
          <FormField
            label="Description"
            name="description"
            as="textarea"
            value={formData.description}
            onChange={handleChange}
            placeholder="Describe your event..."
            rows={4}
            maxLength={2000}
          />
        </div>

        <div className="eventFormHalfWidth">
          <FormField
            label="Start Date"
            name="eventStartDate"
            type="date"
            value={formData.eventStartDate}
            onChange={handleChange}
            min={today}
            required
          />
        </div>

        <div className="eventFormHalfWidth">
          <FormField
            label="Start Time"
            name="eventStartTime"
            type="time"
            value={formData.eventStartTime}
            onChange={handleChange}
            required
          />
        </div>

        <div className="eventFormHalfWidth">
          <FormField
            label="End Date"
            name="eventEndDate"
            type="date"
            value={formData.eventEndDate}
            onChange={handleChange}
            min={formData.eventStartDate || today}
            required
          />
        </div>

        <div className="eventFormHalfWidth">
          <FormField
            label="End Time"
            name="eventEndTime"
            type="time"
            value={formData.eventEndTime}
            onChange={handleChange}
            required
          />
        </div>

        <div className="eventFormFullWidth">
          <FormField
            label="Location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            placeholder="e.g., CGI Erfurt Office"
            required
            maxLength={200}
          />
        </div>

        <div className="eventFormFullWidth">
          <FormField
            label="Maximum Capacity"
            name="maxCapacity"
            type="number"
            value={formData.maxCapacity.toString()}
            onChange={handleChange}
            min="1"
            max="10000"
            required
          />
        </div>

        <div className="eventFormHalfWidth">
          <label className="eventFormCheckboxLabel">
            <input
              type="checkbox"
              name="mealOptionsEnabled"
              checked={formData.mealOptionsEnabled}
              onChange={handleChange}
              className="eventFormCheckbox"
            />
            <span>Enable Meal Options</span>
          </label>
          <p className="eventFormHint">
            Allow attendees to select meal preferences during registration
          </p>
        </div>

        <div className="eventFormHalfWidth">
          <label className="eventFormCheckboxLabel">
            <input
              type="checkbox"
              name="additionalGuestsEnabled"
              checked={formData.additionalGuestsEnabled}
              onChange={handleChange}
              className="eventFormCheckbox"
            />
            <span>Enable Additional Guests</span>
          </label>
          <p className="eventFormHint">
            Allow partners to bring additional adults and children
          </p>
        </div>

        <div className="eventFormHalfWidth">
          <FormField
            label="Registration Deadline"
            name="registrationDeadline"
            type="date"
            value={formData.registrationDeadline}
            onChange={handleChange}
            min={today}
            max={formData.eventStartDate || undefined}
          />
        </div>

        <div className="eventFormHalfWidth">
          <FormField
            label="Deadline Time"
            name="registrationDeadlineTime"
            type="time"
            value={formData.registrationDeadlineTime}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="eventFormActions">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting
            ? "Saving..."
            : isEditMode
              ? "Update Event"
              : "Create Event"}
        </Button>
      </div>
    </form>
  );
};

export default EventForm;
